import { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convert HTTP URL to WebSocket URL
const getWebSocketUrl = () => {
  if (!BACKEND_URL) return null;
  const url = new URL(BACKEND_URL);
  const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${url.host}/ws/inbox`;
};

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const messageHandlersRef = useRef(new Map());

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('WebSocket: No token available');
      return;
    }

    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      console.log('WebSocket: No backend URL configured');
      return;
    }

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket: Connected, authenticating...');
        // Send authentication message
        ws.send(JSON.stringify({
          type: 'auth',
          token: token
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'auth_success':
              console.log('WebSocket: Authenticated successfully');
              setIsConnected(true);
              reconnectAttempts.current = 0;
              break;
            
            case 'error':
              console.error('WebSocket error:', data.message);
              if (data.message === 'Invalid or expired token') {
                // Don't reconnect for auth errors
                reconnectAttempts.current = maxReconnectAttempts;
              }
              break;
            
            case 'unread_count':
              setUnreadCount(data.count);
              break;
            
            case 'new_message':
            case 'conversation_update':
            case 'message_status':  // Handle message delivery status updates
              setLastMessage(data);
              // Trigger registered handlers
              messageHandlersRef.current.forEach((handler) => {
                handler(data);
              });
              break;
            
            case 'pong':
              // Keepalive response, ignore
              break;
            
            default:
              console.log('WebSocket message:', data);
          }
        } catch (e) {
          console.error('WebSocket: Error parsing message', e);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket: Disconnected', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt reconnection with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`WebSocket: Reconnecting in ${delay}ms...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

    } catch (error) {
      console.error('WebSocket: Failed to create connection', error);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    reconnectAttempts.current = maxReconnectAttempts; // Prevent reconnection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const subscribeToConversation = useCallback((conversationId) => {
    sendMessage({
      type: 'subscribe_conversation',
      conversation_id: conversationId
    });
  }, [sendMessage]);

  // Register message handler
  const onMessage = useCallback((id, handler) => {
    messageHandlersRef.current.set(id, handler);
    return () => {
      messageHandlersRef.current.delete(id);
    };
  }, []);

  // Keepalive ping every 30 seconds
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      sendMessage({ type: 'ping' });
    }, 30000);

    return () => clearInterval(pingInterval);
  }, [isConnected, sendMessage]);

  // Auto-connect when token is available
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, []);

  return {
    isConnected,
    lastMessage,
    unreadCount,
    connect,
    disconnect,
    sendMessage,
    subscribeToConversation,
    onMessage
  };
};

export default useWebSocket;
